import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaImages, FaCalendarAlt, FaLock } from 'react-icons/fa';
import { getCreditedPhotos, getImageUrl } from '../services/api';

/**
 * The photos this photographer is credited with taking, including those in
 * events run by an event manager.
 *
 * Read-only on purpose: the event's owner keeps pricing, deletion and the
 * revenue, so this is where a photographer tracks their work, not manages it.
 */
const CreditedPhotos = () => {
  const [photos, setPhotos] = useState([]);
  const [summary, setSummary] = useState({ total_photos: 0, events_count: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    getCreditedPhotos()
      .then(response => {
        setPhotos(response.photos || []);
        setSummary(response.summary || { total_photos: 0, events_count: 0 });
      })
      .catch(err => setError(err.message || 'Impossible de charger vos photos'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="container" style={{ textAlign: 'center', padding: '4rem' }}>
        <div className="spinner"></div>
        <p>Chargement de vos photos...</p>
      </div>
    );
  }

  // Group by event so a photographer sees their work shoot by shoot.
  const byEvent = photos.reduce((groups, photo) => {
    const key = photo.event?.id ?? 'unknown';
    if (!groups[key]) {
      groups[key] = { event: photo.event, photos: [] };
    }
    groups[key].photos.push(photo);
    return groups;
  }, {});

  return (
    <div className="container">
      <div className="dashboard-header">
        <h1>Photos qui me sont attribuées</h1>
        <p>
          {summary.total_photos} photo{summary.total_photos !== 1 ? 's' : ''} sur{' '}
          {summary.events_count} événement{summary.events_count !== 1 ? 's' : ''}
        </p>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.6rem',
        padding: '1rem 1.25rem', marginBottom: '2rem',
        background: 'var(--bg)', borderRadius: '12px',
        color: 'var(--gray)', fontSize: '0.9rem',
      }}>
        <FaLock />
        <span>
          Pour les événements organisés par un tiers, la tarification et la
          suppression restent à l'organisateur.
        </span>
      </div>

      {photos.length === 0 ? (
        <div style={{
          padding: '3rem', textAlign: 'center',
          background: 'var(--surface)', borderRadius: '15px',
          boxShadow: '0 4px 8px var(--shadow)',
        }}>
          <FaImages style={{ fontSize: '4rem', color: 'var(--gray)', marginBottom: '1rem' }} />
          <h3 style={{ color: 'var(--dark-gray)' }}>Aucune photo attribuée</h3>
          <p style={{ color: 'var(--gray)' }}>
            Les photos que vous importez, ou qu'un organisateur vous attribue,
            apparaîtront ici.
          </p>
        </div>
      ) : (
        Object.values(byEvent).map(({ event, photos: eventPhotos }) => (
          <div key={event?.id ?? 'unknown'} style={{ marginBottom: '2.5rem' }}>
            <h3 style={{ color: 'var(--primary-green)', marginBottom: '0.25rem' }}>
              {event?.name || 'Événement inconnu'}
            </h3>
            <p style={{ color: 'var(--gray)', fontSize: '0.9rem', marginTop: 0 }}>
              <FaCalendarAlt />{' '}
              {event?.event_date ? new Date(event.event_date).toLocaleDateString() : 'Date inconnue'}
              {' • '}
              {eventPhotos.length} photo{eventPhotos.length !== 1 ? 's' : ''}
              {event?.photographer?.name && ` • organisé par ${event.photographer.name}`}
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
              gap: '0.75rem',
            }}>
              {eventPhotos.map(photo => (
                <div
                  key={photo.id}
                  style={{
                    borderRadius: '10px', overflow: 'hidden',
                    background: 'var(--bg)', aspectRatio: '1 / 1',
                  }}
                  title={photo.name || `Photo ${photo.id}`}
                >
                  <img
                    src={getImageUrl(photo.filename)}
                    alt={photo.name || `Photo ${photo.id}`}
                    loading="lazy"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                </div>
              ))}
            </div>
          </div>
        ))
      )}

      <div style={{ marginTop: '2rem' }}>
        <Link to="/photographer">← Retour à mes événements</Link>
      </div>
    </div>
  );
};

export default CreditedPhotos;
